/**
 * Runtime resolver for the ViewerConfig descriptor (declared in Python at
 * `inspect_ai.viewer._config`, surfaced end-to-end via codegen in
 * `@tsmono/inspect-common/types`).
 *
 * Callers pass a `ViewerConfig | null | undefined` and a scanner name; the
 * resolver returns a `ResolvedScannerResultView` — an ordered list of
 * rendering-ready fields plus the set of metadata keys the `metadata` section
 * must hide from its dump. Falls back to a built-in default when no descriptor
 * applies.
 */
import type {
  MetadataField,
  ScannerResultField,
  ScannerResultView,
  ViewerConfig,
} from "@tsmono/inspect-common/types";

import {
  kMaxScannerPatterns,
  ScannerGlobBudget,
  scannerGlobMatches,
} from "./scannerGlob";

export type ResolvedField = ScannerResultField | MetadataField;

export interface ResolvedScannerResultView {
  /** Ordered render list (mix of builtin sections + promoted metadata keys). */
  fields: ResolvedField[];
  /**
   * Metadata keys the `metadata` section should omit from its dump:
   * - every promoted `MetadataField.key` already rendered as its own section
   * - every `MetadataField` listed in `exclude_fields` (direct suppression)
   */
  excludedMetadataKeys: string[];
}

/** Built-in section names in the order they render by default. Every other
 * list / set of builtin names in this module is derived from this one. */
const kBuiltinFieldOrder = [
  "explanation",
  "label",
  "value",
  "validation",
  "answer",
  "metadata",
] as const satisfies ReadonlyArray<ScannerResultField["name"]>;

// Typed over `string` so membership checks double as narrowing input.
const kBuiltinNames: ReadonlySet<string> = new Set(kBuiltinFieldOrder);

const isBuiltinName = (value: string): value is ScannerResultField["name"] =>
  kBuiltinNames.has(value);

/** Built-in default order when no scanner pattern matches. */
export const kDefaultFields: readonly ResolvedField[] = kBuiltinFieldOrder.map(
  (name) => ({ kind: "builtin", name, collapsed: false })
);

/** The resolved value returned when no viewer config is provided at all. */
export const kDefaultResolvedView: ResolvedScannerResultView = {
  fields: [...kDefaultFields],
  excludedMetadataKeys: [],
};

const kMetadataPrefix = "metadata.";

export function resolveScannerResultView(
  viewer: ViewerConfig | null | undefined,
  scannerName: string
): ResolvedScannerResultView {
  const entries = scannerResultViewEntries(viewer);
  if (entries.length === 0) return kDefaultResolvedView;

  const matches = rankMatchingEntries(entries, scannerName);
  const mostSpecific = matches[0];
  if (!mostSpecific) return kDefaultResolvedView;

  // `fields` pins a prefix: the user's list renders first (in the listed
  // order), then any builtin sections the user didn't mention render in
  // default order beneath. `exclude_fields` is the only way to hide a
  // section. This matches the intuition that `fields` adds / reorders and
  // `exclude_fields` removes.
  const userFields =
    mostSpecific.view.fields === null || mostSpecific.view.fields === undefined
      ? []
      : coerceFields(mostSpecific.view.fields);
  const mentionedBuiltins = new Set<ScannerResultField["name"]>();
  for (const f of userFields) {
    if (f.kind === "builtin") mentionedBuiltins.add(f.name);
  }
  const unlistedDefaults = kDefaultFields.filter(
    (f) => f.kind === "builtin" && !mentionedBuiltins.has(f.name)
  );
  const baseFields: ResolvedField[] = [...userFields, ...unlistedDefaults];

  // Union exclude_fields across every matching pattern.
  const exclusions = matches.flatMap((m) =>
    coerceFields(m.view.exclude_fields)
  );

  const excludeBuiltinNames = new Set<ScannerResultField["name"]>();
  const directlyExcludedMetadataKeys = new Set<string>();
  for (const f of exclusions) {
    if (f.kind === "builtin") excludeBuiltinNames.add(f.name);
    else directlyExcludedMetadataKeys.add(f.key);
  }

  const fields = baseFields.filter((f) =>
    f.kind === "builtin"
      ? !excludeBuiltinNames.has(f.name)
      : !directlyExcludedMetadataKeys.has(f.key)
  );

  const promotedKeys = new Set<string>();
  for (const f of fields) if (f.kind === "metadata") promotedKeys.add(f.key);

  const excludedMetadataKeys = Array.from(
    new Set<string>([...promotedKeys, ...directlyExcludedMetadataKeys])
  );

  return { fields, excludedMetadataKeys };
}

// ---------------------------------------------------------------------------
// Internals
// ---------------------------------------------------------------------------

interface MatchedEntry {
  pattern: string;
  view: ScannerResultView;
  /** Count of non-wildcard characters — used for specificity ranking. */
  specificity: number;
  /** Insertion order in the original dict (for deterministic tie-breaks). */
  order: number;
}

function scannerResultViewEntries(
  viewer: ViewerConfig | null | undefined
): Array<{ pattern: string; view: ScannerResultView }> {
  if (!viewer) return [];
  const raw = viewer.scanner_result_view;
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
  if (!raw) return [];
  // Bare `ScannerResultView` shorthand for `{"*": view}`.
  if (isScannerResultView(raw)) return [{ pattern: "*", view: raw }];
  const entries: Array<{ pattern: string; view: ScannerResultView }> = [];
  for (const pattern in raw) {
    if (!Object.hasOwn(raw, pattern)) continue;
    if (entries.length === kMaxScannerPatterns) {
      throw new Error(
        "Scanner result view must contain at most 1024 glob patterns."
      );
    }
    entries.push({ pattern, view: raw[pattern]! });
  }
  return entries;
}

function isScannerResultView(v: unknown): v is ScannerResultView {
  if (!v || typeof v !== "object") return false;
  // The bare form is distinguishable from the glob-keyed map by having a
  // top-level `fields` or `exclude_fields` key. (The same heuristic the Python
  // side uses; scanner names "fields" / "exclude_fields" are reserved.)
  return "fields" in v || "exclude_fields" in v;
}

function rankMatchingEntries(
  entries: Array<{ pattern: string; view: ScannerResultView }>,
  scannerName: string
): MatchedEntry[] {
  const matches: MatchedEntry[] = [];
  const budget = new ScannerGlobBudget();
  entries.forEach(({ pattern, view }, order) => {
    if (!scannerGlobMatches(pattern, scannerName, budget)) return;
    matches.push({
      pattern,
      view,
      specificity: specificityOf(pattern),
      order,
    });
  });
  // Most specific first; tie → earliest insertion order wins.
  matches.sort((a, b) => {
    if (a.specificity !== b.specificity) return b.specificity - a.specificity;
    return a.order - b.order;
  });
  return matches;
}

function specificityOf(pattern: string): number {
  // Count non-wildcard characters. `*` and `?` don't count.
  let n = 0;
  for (const ch of pattern) if (ch !== "*" && ch !== "?") n++;
  return n;
}

function coerceFields(
  raw: ReadonlyArray<ScannerResultField | MetadataField | string>
): ResolvedField[] {
  const out: ResolvedField[] = [];
  for (const entry of raw) {
    const coerced = coerceField(entry);
    if (coerced) out.push(coerced);
  }
  return out;
}

/**
 * String shorthand:
 *   `"metadata.<key>"` → `MetadataField(key=<key>)`
 *   `"<builtin>"`      → `ScannerResultField(name=<builtin>)`
 *
 * Entries with an unknown `kind` (e.g. hand-edited eval logs) or invalid
 * builtin names are dropped defensively.
 */
function coerceField(
  entry: ScannerResultField | MetadataField | string
): ResolvedField | null {
  if (typeof entry === "string") {
    if (
      entry.startsWith(kMetadataPrefix) &&
      entry.length > kMetadataPrefix.length
    ) {
      return {
        kind: "metadata",
        key: entry.slice(kMetadataPrefix.length),
        collapsed: false,
      };
    }
    if (isBuiltinName(entry)) {
      return {
        kind: "builtin",
        name: entry,
        collapsed: false,
      };
    }
    return null;
  }
  if (entry.kind === "builtin") {
    if (!kBuiltinNames.has(entry.name)) return null;
    return entry;
  }
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
  if (entry.kind === "metadata") return entry;
  return null;
}
